import { useRef, useEffect } from 'react';

/**
 * A custom hook that returns the previous value of a state or prop.
 * This is useful for comparing current and previous values in effects or render logic.
 *
 * @param value - The current value to track
 * @returns The previous value (undefined on first render)
 *
 * @example
 * ```tsx
 * const Component = ({ count }) => {
 *   const prevCount = usePrevious(count);
 *
 *   useEffect(() => {
 *     if (prevCount !== undefined && count !== prevCount) {
 *       console.log(`Count changed from ${prevCount} to ${count}`);
 *     }
 *   }, [count, prevCount]);
 *
 *   return <div>Current: {count}, Previous: {prevCount}</div>;
 * };
 * ```
 */
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}
